import type {ReactElement} from 'react';
import React from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {AddItemContainer} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-container';
import type {ElementTestId} from '@Tests/tests.types';
import type {ActivityIndicatorProps} from '@Components/activity-indicator/activity-indicator.types';
import {ActivityIndicatorSize, ActivityIndicatorType} from '@Components/activity-indicator/activity-indicator.types';
import styles from './add-item-button.module.scss';

interface AddItemButtonProps {
  id?: string;
  className?: string;
  text: string;
  icon: string;
  isSelected?: boolean;
  onClick?(): void;
  hasMarginBottom?: boolean;
  isDisabled?: boolean;
  hasPulsatingDot?: boolean;
  iconClasses?: string;
  textClasses?: string;
  'data-testid'?: ElementTestId;
  activityIndicatorType?: ActivityIndicatorType;
  boldText?: boolean;
}

export function AddItemButton({
  id = '',
  isDisabled = false,
  onClick = (): void => {},
  className = '',
  isSelected = false,
  hasMarginBottom = true,
  hasPulsatingDot = false,
  textClasses = '',
  boldText = false,
  ...props
}: AddItemButtonProps): ReactElement {
  const getColorClass = (): string => {
    if (isDisabled) {
      return 'content-disabled';
    }
    if (isSelected) {
      return 'content-primary';
    }
    return '';
  };

  const getActivityIndicatorProps = (): ActivityIndicatorProps | undefined => {
    if (hasPulsatingDot) {
      return {
        size: ActivityIndicatorSize.SIZE_16,
        type: ActivityIndicatorType.NEW_PULSE,
      };
    }
    if (props.activityIndicatorType) {
      return {
        size: ActivityIndicatorSize.SIZE_16,
        type: props.activityIndicatorType,
      };
    }

    return undefined;
  };

  return (
    <AddItemContainer
      onClick={(): void => {
        if (onClick && !isDisabled) {
          onClick();
        }
      }}
      id={id}
      isDisabled={isDisabled}
      isSelected={isSelected}
      data-testid={props['data-testid']}
      hasMarginBottom={hasMarginBottom}
      className={className}
      activityIndicatorProps={getActivityIndicatorProps()}
    >
      <Icon icon={props.icon} size={IconSize.SIZE_ICON_24} className={`${getColorClass()} ${props.iconClasses ?? ''}`} />
      <Text bold={isSelected} val={props.text} size={TextSize.XXSMALL} className={`${getColorClass()} ${styles.overflow} ${textClasses}`} />
    </AddItemContainer>
  );
}
